import { useMemo, useState } from "react";
import {
  useCreate,
  useGetList,
  useNotify,
  useRefresh,
  useUpdate,
} from "ra-core";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Building2,
  CalendarCheck2,
  Clock,
  FileText,
  Plus,
  Search,
} from "lucide-react";
import { PageHeader } from "../common/PageHeader";

type PdcRow = {
  id: number;
  reference: string;
  customer_id: number | null;
  invoice_id: number | null;
  cheque_number: string;
  bank_name: string | null;
  drawer_name: string | null;
  cheque_date: string;
  amount: number;
  status: "custody" | "realized" | "bounced" | "cancelled";
  realized_at: string | null;
};

type CompanyOpt = { id: number; name: string };

const fmtINR = (n: number | null | undefined) =>
  `₹${Number(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const fmtDate = (iso: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

const Label = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <label className={`block text-sm font-medium leading-none ${className ?? ""}`}>
    {children}
  </label>
);

export const PDCTrackingPage = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [create] = useCreate();
  const [update] = useUpdate();
  const [search, setSearch] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionBusy, setActionBusy] = useState<number | null>(null);
  const [form, setForm] = useState({
    cheque_number: "",
    drawer_name: "",
    bank_name: "",
    customer_id: "",
    cheque_date: "",
    amount: "",
  });

  const { data, isLoading } = useGetList<PdcRow>("pdc_cheques", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "cheque_date", order: "ASC" },
  });

  const { data: companies } = useGetList<CompanyOpt>("companies", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "name", order: "ASC" },
  });

  const rows = useMemo(
    () =>
      (data ?? []).filter((row) =>
        search
          ? [row.cheque_number, row.drawer_name, row.bank_name, row.reference]
              .filter(Boolean)
              .some((field) =>
                (field as string).toLowerCase().includes(search.toLowerCase()),
              )
          : true,
      ),
    [data, search],
  );

  const totalFace = useMemo(
    () =>
      rows
        .filter((r) => r.status === "custody")
        .reduce((acc, r) => acc + Number(r.amount ?? 0), 0),
    [rows],
  );

  const handleRegister = async () => {
    if (!form.cheque_number || !form.amount || !form.cheque_date) {
      notify("Cheque number, amount and date are required.", { type: "warning" });
      return;
    }
    setSubmitting(true);
    try {
      await create(
        "pdc_cheques",
        {
          data: {
            cheque_number: form.cheque_number,
            drawer_name: form.drawer_name || null,
            bank_name: form.bank_name || null,
            customer_id: form.customer_id ? Number(form.customer_id) : null,
            cheque_date: form.cheque_date,
            amount: Number(form.amount),
            status: "custody",
          },
        },
        { returnPromise: true },
      );
      notify("PDC registered.", { type: "success" });
      setSheetOpen(false);
      setForm({
        cheque_number: "",
        drawer_name: "",
        bank_name: "",
        customer_id: "",
        cheque_date: "",
        amount: "",
      });
      refresh();
    } catch (error) {
      notify(`Failed: ${(error as Error).message}`, { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const setStatus = async (
    row: PdcRow,
    status: "realized" | "bounced" | "cancelled" | "custody",
  ) => {
    setActionBusy(row.id);
    try {
      await update(
        "pdc_cheques",
        {
          id: row.id,
          data: {
            status,
            realized_at: status === "realized" ? new Date().toISOString() : null,
          },
          previousData: row,
        },
        { returnPromise: true },
      );
      notify(`PDC marked ${status}.`, { type: "success" });
      refresh();
    } catch (error) {
      notify(`Failed: ${(error as Error).message}`, { type: "error" });
    } finally {
      setActionBusy(null);
    }
  };

  return (
    <div className="p-6 bg-muted/30 min-h-screen">
      <div className="max-w-7xl mx-auto">
      <PageHeader
        title="PDC Tracking Ledger"
        description="Post-dated cheques in custody. Realized automatically when their date arrives."
      />

      <div className="flex gap-4 mb-6 overflow-x-auto pb-2">
        <div className="min-w-[220px] flex-1 bg-card p-5 rounded-xl border border-border shadow-sm">
          <p className="text-muted-foreground text-[10px] font-extrabold uppercase tracking-wider">
            Active In Custody
          </p>
          <h3 className="text-3xl font-black mt-1 text-foreground">
            {rows.filter((r) => r.status === "custody").length}{" "}
            <span className="text-sm text-muted-foreground font-bold">Instruments</span>
          </h3>
        </div>
        <div className="min-w-[220px] flex-1 bg-card p-5 rounded-xl border-b-4 border-b-emerald-500 shadow-sm">
          <p className="text-muted-foreground text-[10px] font-extrabold uppercase tracking-wider">
            Total Face Reserve
          </p>
          <h3 className="text-3xl font-black mt-1 text-emerald-600">
            {fmtINR(totalFace)}
          </h3>
        </div>
      </div>

      <Card className="shadow-none border border-border bg-card overflow-hidden">
        <CardHeader className="border-b bg-muted/50 flex flex-row items-center justify-between py-4">
          <CardTitle className="text-base font-bold flex items-center gap-2 text-foreground">
            <CalendarCheck2 className="h-5 w-5 text-muted-foreground" /> Maturing Instrument
            Registry
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative max-w-xs hidden md:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search instruments..."
                className="h-8 pl-8 border-input text-xs shadow-none w-[180px] bg-card"
              />
            </div>
            <Button
              onClick={() => setSheetOpen(true)}
              className="bg-slate-900 hover:bg-slate-800 font-bold text-white shadow-md text-xs h-8 gap-1.5"
            >
              <Plus className="h-3.5 w-3.5" /> Register PDC
            </Button>
          </div>
        </CardHeader>

        <div className="w-full overflow-x-auto border border-border rounded-xl overflow-hidden">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-muted border-b-2 border-border">
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground border-r border-border">
                  Cheque No.
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground border-r border-border">
                  Drawer
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground border-r border-border">
                  Drawee Bank
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground border-r border-border">
                  Cheque Date
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground border-r border-border text-right">
                  Value
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground text-right">
                  Status / Action
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-12 text-center text-sm text-muted-foreground"
                  >
                    Loading PDC ledger…
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-12 text-center text-sm text-muted-foreground"
                  >
                    No PDC instruments yet.
                  </td>
                </tr>
              )}
              {rows.map((row) => {
                const isBusy = actionBusy === row.id;
                return (
                  <tr
                    key={row.id}
                    className={`${row.status !== "custody" ? "opacity-70 bg-muted" : "even:bg-muted/50 hover:bg-muted/50"} border-b border-border transition-all duration-300`}
                  >
                    <td className="px-4 py-3.5 text-sm border-r border-border font-mono font-bold text-foreground align-middle">
                      {row.cheque_number}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-border font-bold text-foreground align-middle">
                      {row.drawer_name ?? "—"}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-border text-muted-foreground align-middle">
                      <span className="flex items-center gap-1.5">
                        <Building2 className="h-3.5 w-3.5 opacity-60" />
                        {row.bank_name ?? "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-border text-muted-foreground align-middle">
                      {fmtDate(row.cheque_date)}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-border text-right font-black text-foreground align-middle">
                      {fmtINR(row.amount)}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-right align-middle">
                      {row.status === "realized" && (
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 font-bold shadow-none">
                          ✓ Realized
                        </Badge>
                      )}
                      {row.status === "bounced" && (
                        <div className="flex gap-1.5 justify-end items-center">
                          <Badge className="bg-rose-100 text-rose-800 border-rose-200 font-bold shadow-none">
                            Bounced
                          </Badge>
                          <Button
                            disabled={isBusy}
                            onClick={() => setStatus(row, "custody")}
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs font-bold border-amber-200 text-amber-700 hover:bg-amber-50"
                            title="Cheque was re-presented to the bank — put it back in custody to re-track it toward realization"
                          >
                            Re-present
                          </Button>
                        </div>
                      )}
                      {row.status === "cancelled" && (
                        <Badge className="bg-slate-200 text-foreground border-input font-bold shadow-none">
                          Cancelled
                        </Badge>
                      )}
                      {row.status === "custody" && (
                        <div className="flex gap-1.5 justify-end">
                          <Button
                            disabled={isBusy}
                            onClick={() => setStatus(row, "bounced")}
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs font-bold border-rose-200 text-rose-700 hover:bg-rose-50"
                          >
                            Bounce
                          </Button>
                          <Button
                            disabled={isBusy}
                            onClick={() => setStatus(row, "cancelled")}
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs font-bold border-border text-muted-foreground hover:bg-muted"
                          >
                            Cancel
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-[440px] p-0 flex flex-col bg-card border-l border-border h-full">
          <SheetHeader className="p-6 bg-slate-900 text-white">
            <SheetTitle className="text-white text-xl font-bold flex items-center gap-2">
              <FileText className="h-5 w-5 text-emerald-400" /> Register Physical PDC
            </SheetTitle>
            <SheetDescription className="text-muted-foreground font-medium mt-1">
              Insert incoming post-dated instrument into safe custody.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            <div className="space-y-1.5">
              <Label className="text-muted-foreground font-bold text-sm">Customer</Label>
              <Select
                value={form.customer_id}
                onValueChange={(value) => setForm({ ...form, customer_id: value })}
              >
                <SelectTrigger className="w-full h-11 font-bold text-foreground">
                  <SelectValue placeholder="— select —" />
                </SelectTrigger>
                <SelectContent>
                  {(companies ?? []).map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-muted-foreground font-bold text-sm">
                Drawer Name
              </Label>
              <Input
                value={form.drawer_name}
                onChange={(e) =>
                  setForm({ ...form, drawer_name: e.target.value })
                }
                placeholder="e.g. Reliance Industries Ltd."
                className="h-11 border-input font-bold"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-muted-foreground font-bold text-sm">
                  Cheque No.
                </Label>
                <Input
                  value={form.cheque_number}
                  onChange={(e) =>
                    setForm({ ...form, cheque_number: e.target.value })
                  }
                  placeholder="123456"
                  className="h-11 border-input font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-muted-foreground font-bold text-sm">Amount</Label>
                <Input
                  type="number"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.00"
                  className="h-11 border-input font-bold"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-muted-foreground font-bold text-sm">
                  Drawee Bank
                </Label>
                <Input
                  value={form.bank_name}
                  onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                  placeholder="ICICI / HDFC / etc."
                  className="h-11 border-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-muted-foreground font-bold text-sm">
                  Cheque Date
                </Label>
                <Input
                  type="date"
                  value={form.cheque_date}
                  onChange={(e) =>
                    setForm({ ...form, cheque_date: e.target.value })
                  }
                  className="h-11 border-input"
                />
              </div>
            </div>

            <div className="p-4 bg-muted rounded-xl border border-border text-xs font-bold flex items-center gap-2.5 text-muted-foreground">
              <Clock className="h-4 w-4 text-primary" />
              <span>
                System auto-realises cheques on or after the maturity date (daily
                02:00 IST).
              </span>
            </div>
          </div>

          <SheetFooter className="p-6 bg-muted border-t">
            <Button
              onClick={handleRegister}
              disabled={submitting}
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-lg text-lg gap-2"
            >
              {submitting ? "Saving…" : "Commit to Ledger"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};
